import type { LegalDocument } from '../config/legal'
import { useText } from '../i18n/useText'

export interface LegalPageProps {
  document: LegalDocument
  /** "Last updated", already translated — `ul.legal.updated` in the catalogue. */
  updatedLabel: string
}

/**
 * The privacy policy and the terms, rendered from `config/legal.ts`.
 *
 * One component and one source for five apps, because there is one Foundation and a
 * privacy policy is a statement about what an organisation does. Five copies would become
 * five policies the first time one was amended and the others were not — and nobody would
 * notice, because nobody reads four of them. Ground rule 5, arriving at the same place a
 * legal argument does.
 *
 * It reads `useText()` rather than taking resolved strings, which is unlike most of the
 * design system: the two documents are dozens of paragraphs, and threading each one
 * through an app's `useShellContent` would put the whole of a privacy policy in five
 * places whose only job is to call `s()` on it. The three *chrome* strings — the trail's
 * label, and "last updated" — still come in as props, because those are catalogue keys
 * that belong to the app.
 *
 * `useText()` needs a `LocaleProvider` above it, and all five apps have had one since
 * 2026-09-19. It is the first design-system component to require one.
 */
export function LegalPage({ document, updatedLabel }: LegalPageProps) {
  const s = useText()

  return (
    <>
      {/*
        No banner (2026-09-19). These two pages opened on `Breadcrumb`'s dark photographic
        band under the document's name — a screen of chrome and a stock photograph in front
        of a privacy policy, which is the one kind of page a reader arrives at already
        knowing what it is and wanting only to read it.

        `.ul-plain-page-top` in its place: it reserves the absolutely-positioned header's
        height and washes the ground beneath it, which is all the top of this page needs.
        The title becomes the page's `<h1>` here rather than in the banner — `audit:seo`
        fails a page with any number other than one.
      */}
      <section className="ul-section-spacing ul-plain-page-top">
        <div className="ul-container">
          <article className="ul-legal wow animate__fadeInUp">
            <h1 className="ul-plain-page-title">{s(document.title)}</h1>
            {/*
              A real `<time>` with a machine-readable date. It is the one fact on the page
              that tells a reader whether anybody has looked at this since the site opened,
              and the rendered form follows the reader's locale rather than being written
              out in one language — see `intlLocale` in `LOCALE_INFO`, which is why the tag
              is formatted by the browser and not by us.
            */}
            <p className="ul-legal-updated">
              {updatedLabel} <time dateTime={document.updated}>{document.updated}</time>
            </p>

            {document.intro.map((paragraph) => (
              <p className="ul-legal-intro" key={paragraph.en}>
                {s(paragraph)}
              </p>
            ))}

            {document.sections.map((section) => (
              <section className="ul-legal-section" key={section.heading.en}>
                {/* `<h2>`: the page's one `<h1>` is `Breadcrumb`'s title. `audit:seo` fails
                    a page with any other number, and a policy is exactly the shape of page
                    somebody would give a second one to by accident. */}
                <h2 className="ul-legal-heading">{s(section.heading)}</h2>
                {section.body.map((paragraph) => (
                  <p className="ul-legal-body" key={paragraph.en}>
                    {s(paragraph)}
                  </p>
                ))}
              </section>
            ))}
          </article>
        </div>
      </section>
    </>
  )
}
